import React, { useState, useEffect } from "react";
import FaqHeroSection from "./FaqHeroSection";

type FAQItem = {
  id: number;
  question: string;
  answer: string;
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Who is John Bostjancic?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "John Bostjancic is an accomplished CFO with deep expertise in financial leadership, specializing in the healthcare and medical device sectors, driving growth and strategic innovation..",
      },
    },
    {
      "@type": "Question",
      name: "What are John Bostjancic’s areas of expertise?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "John Bostjancic is a finance expert in investor relations, risk management, organizational transformation, and innovation.",
      },
    },
    {
      "@type": "Question",
      name: "How much experience does John Bostjancic have?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "With more than 30 years of experience in finance, John Bostjancic has held leadership positions across various industries.",
      },
    },
    {
      "@type": "Question",
      name: "What industries has John Bostjancic worked in?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "His expertise covers healthcare, medical devices, and other sectors demanding high-level financial leadership.",
      },
    },
    {
      "@type": "Question",
      name: "Is John Bostjancic available for consulting?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, he is available for interim CFO roles and consulting projects.",
      },
    },
    {
      "@type": "Question",
      name: "What is John Bostjancic’s leadership style?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "John is known for his operational focus, strategic thinking, and commitment to mentoring and innovation",
      },
    },
    {
      "@type": "Question",
      name: "Where is John Bostjancic based?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "He currently resides in Carlsbad, California.",
      },
    },
  ],
};

const faqData: FAQItem[] = [
  {
    id: 1,
    question: "Who is John Bostjancic?",
    answer:
      "John Bostjancic is an accomplished CFO with deep expertise in financial leadership, specializing in the healthcare and medical device sectors, driving growth and strategic innovation..",
  },
  {
    id: 2,
    question: "What are John Bostjancic’s areas of expertise?",
    answer:
      "John Bostjancic is a finance expert in investor relations, risk management, organizational transformation, and innovation.",
  },
  {
    id: 3,
    question: "How much experience does John Bostjancic have?",
    answer:
      "With more than 30 years of experience in finance, John Bostjancic has held leadership positions across various industries.",
  },
  {
    id: 4,
    question: "What industries has John Bostjancic worked in?",
    answer:
      "His expertise covers healthcare, medical devices, and other sectors demanding high-level financial leadership.",
  },
  {
    id: 5,
    question: "Is John Bostjancic available for consulting?",
    answer:
      "Yes, he is available for interim CFO roles and consulting projects.",
  },
  {
    id: 6,
    question: "What is John Bostjancic’s leadership style?",
    answer:
      "John is known for his operational focus, strategic thinking, and commitment to mentoring and innovation.",
  },
  {
    id: 7,
    question: "Where is John Bostjancic based?",
    answer: "He currently resides in Carlsbad, California.",
  },
];

const Faq = () => {
  const [activeId, setActiveId] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setActiveId(activeId === id ? null : id); // Toggle active state
  };

  useEffect(() => {
    const metaTag = document.querySelector("meta[name='description']");
    document.title = "Frequently Asked Questions - FAQ | John Bostjancic, CFO";

    if (metaTag) {
      document.head.appendChild(metaTag);
      metaTag.setAttribute(
        "content",
        "Explore FAQs about John Bostjancic, CFO at Sonendo, Inc., covering finance, risk management, and business growth strategies."
      );
    }

    let linkTag = document.querySelector("link[rel='canonical']");
    if (linkTag) {
      linkTag.setAttribute("href", "https://johnbostjancic.co/faq");
    } else {
      linkTag = document.createElement("link");
      (linkTag as HTMLLinkElement).rel = "canonical";
      (linkTag as HTMLLinkElement).href = "https://johnbostjancic.co/faq";
    }

    if (!document.head.contains(linkTag)) {
      document.head.appendChild(linkTag);
    }

    //keyWords
    let keyWordTag = document.querySelector("meta[name='keywords']");
    if (!keyWordTag) {
      keyWordTag = document.createElement("meta");
      (keyWordTag as HTMLMetaElement).name = "keywords";
      document.head.appendChild(keyWordTag);
    }
    keyWordTag.setAttribute("content", "FAQ John Bostjancic");

    // Schema
    // Remove existing schema if present
    const existingSchema = document.querySelector(
      "script[type='application/ld+json']"
    );
    if (existingSchema) {
      existingSchema.setAttribute("type", "application/ld+json");
      existingSchema.textContent = JSON.stringify(faqSchema);
    } else {
      const scriptTag = document.createElement("script");
      scriptTag.setAttribute("type", "application/ld+json");
      scriptTag.textContent = JSON.stringify(faqSchema);

      document.head.appendChild(scriptTag);
    }
  }, []);

  return (
    <>
      <FaqHeroSection />
      <section className="max-w-screen-2xl mx-auto px-5 lg:px-20 py-8 xl:py-20">
        <div className="w-full bg-[#F8F9FF] flex justify-center items-center px-5 sm:px-0 py-8 lg:py-20 rounded-[50px]">
          <div className="lg:w-[60%]">
            {faqData.map((item) => (
              <div
                key={item.id}
                className="border rounded-lg mb-4 border-black overflow-hidden shadow-sm"
              >
                {/* Question */}
                <button
                  onClick={() => toggleFAQ(item.id)}
                  className={`w-full text-left p-6 montserrat-medium sm:text-lg flex justify-between items-center transition-all duration-300 ${
                    activeId === item.id
                      ? "bg-primary text-white"
                      : "bg-transparent text-secondary"
                  }`}
                >
                  {item.question}
                  <span className="text-xl montserrat-regular">
                    {activeId === item.id ? "−" : "+"}
                  </span>
                </button>

                {/* Answer */}
                {activeId === item.id && (
                  <div className="p-4  text-secondary montserrat-regular ">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Faq;
